import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthProvider';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Avatar } from '../ui/Avatar';
import { useTeam } from '../../lib/api/tasks';
import { MosyUser } from '../../lib/types';
import {
  Comment,
  useComments,
  useCreateComment,
  useDeleteComment,
} from '../../lib/api/comments';

const MENTION_RE = /(@[\w.+\-]+)/g;

const relTime = (iso: string): string => {
  const t = new Date(iso).getTime();
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

/** Split the body so resolved @mentions render as chips while unresolved
 *  ones (e.g. @nobody) stay as plain text. */
const renderBody = (body: string, mentions: MosyUser[]): ReactNode[] => {
  const known = new Set(mentions.map((u) => u.username.toLowerCase()));
  return body.split(MENTION_RE).map((part, i) => {
    if (part.startsWith('@')) {
      const handle = part.slice(1).toLowerCase();
      if (known.has(handle)) {
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              background: 'var(--sitewide-bg)',
              color: 'var(--sitewide)',
              padding: '0 4px',
              borderRadius: 3,
              fontWeight: 600,
            }}
          >
            {part}
          </span>
        );
      }
    }
    return <span key={i}>{part}</span>;
  });
};

const CommentRow = ({
  comment,
  ownByCurrent,
  onDelete,
}: {
  comment: Comment;
  ownByCurrent: boolean;
  onDelete: () => void;
}) => {
  const a = comment.author_details;
  const initials =
    a?.initials || ((a?.first_name?.[0] ?? '') + (a?.last_name?.[0] ?? '')).toUpperCase() || '?';
  const name = a
    ? (a.display_name || `${a.first_name} ${a.last_name}`.trim() || a.username)
    : '(unknown)';
  return (
    <div style={{ display: 'flex', gap: 10, paddingTop: 10 }}>
      <Avatar initials={initials} imageUrl={a?.avatar ?? null} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{name}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{relTime(comment.created_at)}</span>
          {ownByCurrent && (
            <button
              type="button"
              onClick={onDelete}
              title="Delete this comment"
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--ink-4)',
                padding: 2,
                display: 'inline-flex',
              }}
            >
              <Icon name="x" size={11} />
            </button>
          )}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--ink-2)',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
            marginTop: 2,
          }}
        >
          {renderBody(comment.body, comment.mention_details ?? [])}
        </div>
      </div>
    </div>
  );
};

interface Props {
  /** Either set assetId OR taskId — the section attaches comments to that parent. */
  assetId?: number;
  taskId?: string;
}

export const CommentsSection = ({ assetId, taskId }: Props) => {
  const { user } = useAuth();
  const parent = assetId != null
    ? { assetId } as const
    : taskId
    ? { taskId } as const
    : null;
  const { data: comments = [], isLoading } = useComments(parent);
  const { data: team = [] } = useTeam();
  const createComment = useCreateComment();
  const deleteComment = useDeleteComment();
  const toast = useToast();
  const [body, setBody] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Mention picker state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(0);
  const [highlight, setHighlight] = useState(0);

  const filteredTeam = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    const match = (u: { username: string; first_name: string; last_name: string; email: string }) =>
      !q ||
      u.username.toLowerCase().includes(q) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q);
    return team.filter(match).slice(0, 6);
  }, [mentionQuery, team]);

  useEffect(() => setHighlight(0), [mentionQuery, filteredTeam.length]);

  const updateMentionState = (text: string, cursor: number) => {
    const before = text.slice(0, cursor);
    const m = before.match(/@([\w.+\-]*)$/);
    if (m) {
      setMentionStart(cursor - m[0].length);
      setMentionQuery(m[1]);
    } else {
      setMentionQuery(null);
    }
  };

  const onChangeBody = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setBody(v);
    updateMentionState(v, e.target.selectionStart ?? v.length);
  };

  const insertMention = (u: MosyUser) => {
    if (mentionQuery === null) return;
    const tokenLen = mentionQuery.length + 1; // include the '@'
    const before = body.slice(0, mentionStart);
    const after = body.slice(mentionStart + tokenLen);
    const inserted = `@${u.username} `;
    const next = before + inserted + after;
    const newCursor = (before + inserted).length;
    setBody(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
    });
  };

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    if (!parent) return;
    try {
      const payload = parent.assetId != null
        ? { asset: parent.assetId, body: trimmed }
        : { task: parent.taskId, body: trimmed };
      await createComment.mutateAsync(payload);
      setBody('');
      setMentionQuery(null);
    } catch (err) {
      console.error(err);
      toast.push('Could not post comment');
    }
  };

  const onDelete = async (c: Comment) => {
    if (!parent) return;
    try {
      await deleteComment.mutateAsync({ id: c.id, parent });
    } catch (err) {
      console.error(err);
      toast.push('Could not delete comment');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && filteredTeam.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % filteredTeam.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (h - 1 + filteredTeam.length) % filteredTeam.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredTeam[highlight]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        background: 'var(--bg-subtle)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--ink-4)',
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Comments {comments.length > 0 && <span style={{ color: 'var(--ink-5)' }}>· {comments.length}</span>}
      </div>

      {isLoading ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
          No comments yet — leave context for whoever picks this up next. Type @ to mention a teammate.
        </div>
      ) : (
        <div>
          {comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              ownByCurrent={!!user && c.author === user.user_id}
              onDelete={() => onDelete(c)}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <textarea
            ref={taRef}
            className="input"
            rows={2}
            placeholder="Leave a note for the team — type @ to tag someone"
            value={body}
            onChange={onChangeBody}
            onKeyDown={onKeyDown}
            onClick={(e) =>
              updateMentionState(
                (e.target as HTMLTextAreaElement).value,
                (e.target as HTMLTextAreaElement).selectionStart ?? 0,
              )
            }
            style={{ resize: 'vertical', width: '100%' }}
          />
          {mentionQuery !== null && filteredTeam.length > 0 && (
            <div
              role="listbox"
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,.10)',
                zIndex: 50,
                padding: 4,
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              {filteredTeam.map((u, i) => {
                const display =
                  u.display_name || `${u.first_name} ${u.last_name}`.trim() || u.username;
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(u);
                    }}
                    onMouseEnter={() => setHighlight(i)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 8px',
                      width: '100%',
                      background: i === highlight ? 'var(--bg-hover)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: 6,
                      textAlign: 'left',
                    }}
                  >
                    <Avatar
                      initials={u.initials || display.slice(0, 2).toUpperCase()}
                      imageUrl={u.avatar}
                      size={20}
                    />
                    <span style={{ fontSize: 12.5, color: 'var(--ink)' }}>{display}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 4 }}>
                      @{u.username}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <Button
          variant="primary"
          onClick={submit}
          disabled={!body.trim() || createComment.isPending}
        >
          Post
        </Button>
      </div>
    </div>
  );
};

export default CommentsSection;
